import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { GALLERY, useLang } from "@/i18n";

const FILTERS = ["todos", "escenario", "eventos", "clases", "comunidad"];

export default function Gallery() {
  const { t } = useLang();
  const g = t.gallery;
  const [filter, setFilter] = useState("todos");
  const [lightbox, setLightbox] = useState(null);

  const items = GALLERY.filter((img) => filter === "todos" || img.cat === filter);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const step = (dir) => {
    if (!lightbox) return;
    const idx = items.findIndex((it) => it.url === lightbox.url);
    const next = items[(idx + dir + items.length) % items.length];
    setLightbox(next);
  };

  return (
    <section id="galeria" className="px-5 py-24 sm:px-8 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent"
        >
          {g.label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl"
        >
          {g.title}
        </motion.h2>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`gallery-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
                filter === f ? "border-accent bg-accent text-white shadow-[0_0_20px_rgba(255,0,122,0.4)]" : "border-white/15 text-zinc-400 hover:border-accent hover:text-accent"
              }`}
            >
              {g.filters[f]}
            </button>
          ))}
        </div>

        <div className="mt-10 flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:auto-rows-[200px] sm:gap-4 lg:grid-cols-4 lg:auto-rows-[220px]">
          {items.map((img, i) => (
            <motion.figure
              key={img.url}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setLightbox(img)}
              className={`group relative cursor-zoom-in overflow-hidden rounded-xl border-2 border-white/15 transition-all duration-500 hover:border-accent hover:shadow-[0_0_35px_rgba(255,0,122,0.3)] ${img.span}`}
            >
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                className="w-full object-cover sm:h-full"
              />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_40%,rgba(8,8,10,0.55)_100%)] opacity-70 transition-opacity duration-500 group-hover:opacity-20" />
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          >
            <button
              type="button"
              data-testid="gallery-lightbox-close"
              aria-label={g.close}
              onClick={() => setLightbox(null)}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-accent hover:text-accent"
            >
              <X size={20} />
            </button>
            <button
              type="button"
              aria-label="Anterior"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white transition-colors hover:border-accent hover:text-accent sm:left-6"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-5xl"
            >
              <img src={lightbox.url} alt={lightbox.alt} className="max-h-[76vh] w-auto max-w-full rounded-xl border-2 border-accent/50 object-contain shadow-[0_0_60px_rgba(255,0,122,0.35)]" />
              <figcaption className="mt-3 text-center font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">{lightbox.alt}</figcaption>
            </motion.figure>
            <button
              type="button"
              aria-label="Siguiente"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white transition-colors hover:border-accent hover:text-accent sm:right-6"
            >
              <ChevronRight size={22} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}